import type { SessionStatus } from '@UI/Store/AuthenticatedUser';
import useAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useAuthenticatedUser';
import type * as React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

export type ChallengePurpose = 'verification' | 'enrollment';

/**
 * `enrollment` also accepts `'authenticated'`: confirming enrollment opens the session in the same
 * call that returns the one-time recovery codes, so `sessionStatus` flips to `'authenticated'`
 * before the screen has had a chance to display them. Rejecting `'authenticated'` here would bounce
 * the visitor away mid-flow, before the mandatory confirmation step — the one and only exit this
 * screen offers. The sensitive endpoints stay guarded server-side by the MFA challenge cookie
 * regardless of what this route accepts, so widening the accepted status here trades nothing away.
 */
const ACCEPTED_STATUSES: Record<ChallengePurpose, readonly SessionStatus[]> = {
	verification: ['mfa_required'],
	enrollment: ['mfa_enrollment_required', 'authenticated'],
};

type Props = {
	readonly purpose: ChallengePurpose;
	readonly children: React.ReactNode;
};

/**
 * Waits for the session probe to resolve (`sessionStatus !== 'unknown'`) before deciding, exactly
 * like `RequireSession`. Rendering `null` while waiting is safe: whoever reaches a challenge-guarded
 * route already has a partial session in flight.
 */
const RequireChallenge: React.FC<Props> = ({ purpose, children }) => {
	const { sessionStatus } = useAuthenticatedUser();
	const location = useLocation();

	if (sessionStatus === 'unknown') {
		return null;
	}

	if (!ACCEPTED_STATUSES[purpose].includes(sessionStatus)) {
		return <Navigate to="/login" state={location.state} replace />;
	}

	return children;
};

export default RequireChallenge;
